import clsx from "clsx";
import React, {
  FunctionComponent,
  MouseEvent,
  useEffect,
  useId,
  useState,
} from "react";
import { graphql, useFragment } from "react-relay";

import {
  AdminContainer_resolvedResource$data,
  AdminContainer_resolvedResource$key,
} from "@framework/__generated__/relay/AdminContainer_resolvedResource.graphql";
import { useTheme } from "@framework/components/app/ThemeProvider";
import {
  ADMIN_DEV_PHRASE,
  ADMIN_EDIT_SHORTCUT,
  ADMIN_SECRET,
} from "@framework/config/app.config";
import { CORAL_ORIGIN } from "@framework/config/coral.config";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import CogIcon from "@voxmedia/duet-ui/components/icon/CogIcon";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";
import { getCookie, setCookie } from "@voxmedia/duet-utils/cookie";

import * as styles from "./AdminContainer.css";

const ADMIN_GEAR_COOKIE = "duet-admin-gear";

interface Props {
  resolvedResource?: AdminContainer_resolvedResource$key;
}

const selectSelf = (event: MouseEvent<HTMLInputElement>) => {
  event.currentTarget.select();
};

const AdminDevItem: FunctionComponent<{
  name: string;
  value: string | number;
  base64?: boolean;
}> = ({ name, value, base64 }) => {
  const id = useId();
  return (
    <>
      <li className={styles.devModeItem}>
        <label className={styles.devModeLabel} htmlFor={id}>
          {name}
        </label>
        <input
          className={styles.devModeValue}
          type="text"
          readOnly
          id={id}
          value={value}
          onClick={selectSelf}
        />
      </li>
      {base64 && (
        <li className={styles.devModeItem}>
          <label className={styles.devModeLabel} htmlFor={`decoded${id}`}>
            {name} (decoded)
          </label>
          <input
            className={styles.devModeValue}
            type="text"
            readOnly
            id={`decoded${id}`}
            value={atob(value as string)}
            onClick={selectSelf}
          />
        </li>
      )}
    </>
  );
};

const AdminLinkItem: FunctionComponent<{
  href: string;
  children: React.ReactNode;
}> = ({ href, children }) => (
  <li>
    <a className={styles.menuItem} href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  </li>
);

/**
 * Generates the Resource API URL for supported resource types.
 * @param params - Object containing node data
 * @param params.node - The resolved resource node
 * @returns Resource API URL or null if not supported
 */
function getResourceApiUrl({
  node,
}: {
  node: AdminContainer_resolvedResource$data | null | undefined;
}) {
  if (!node) {
    return null;
  }

  /**
   * Converts typename to kebab-case resource path
   * e.g., "VideoPostResourceType" -> "video-post"
   */
  const toKebabCase = (typename: string) => {
    return typename
      .replace("ResourceType", "")
      .replace(/([a-z])([A-Z])/g, "$1-$2")
      .toLowerCase();
  };

  const getContentResourcePath = (nodeId: string) => {
    return `/api/resource/latest/content/${toKebabCase(node.__typename)}/${nodeId}`;
  };

  const getTaxonomyResourcePath = (nodeId: string) => {
    return `/api/resource/latest/${toKebabCase(node.__typename)}/${nodeId}`;
  };

  switch (node.__typename) {
    case "MapResourceType":
      return `${getContentResourcePath(node.id)}?includeFullVenueData=true`;
    case "AuthorProfileResourceType":
    case "CategoryResourceType":
    case "SuperCatResourceType":
      return getTaxonomyResourcePath(node.id);
    case "DiningReportResourceType":
    case "EmbedStoryResourceType":
    case "PackageResourceType":
    case "PageResourceType":
    case "PostResourceType":
    case "QuickPostResourceType":
    case "StreamResourceType":
    case "VenueResourceType":
    case "VideoPostResourceType":
      return getContentResourcePath(node.id);
    default:
      return null;
  }
}

/**
 * Determines the edit context for a given resource node.
 * @param params - Object containing API origins and node data
 * @param params.communityApiOrigin - The community API origin
 * @param params.primaryCommunityApiOrigin - The primary community API origin
 * @param params.node - The resolved resource node
 * @returns Edit context with type and URL, or null if no node
 */
function determineEditContext({
  communityApiOrigin,
  primaryCommunityApiOrigin,
  node,
}: {
  communityApiOrigin: string;
  primaryCommunityApiOrigin: string;
  node: AdminContainer_resolvedResource$data | null | undefined;
}) {
  if (!node) {
    return null;
  }

  switch (node.__typename) {
    case "EmbedStoryResourceType":
      return {
        type: "Embed Story",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "HomepageResourceType":
      return {
        type: "Homepage",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "PackageResourceType":
      return {
        type: "Package",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "PageResourceType":
      return {
        type: "Page",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "PostResourceType":
      return {
        type: "Post",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "StreamResourceType":
      return {
        type: "Stream",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "AuthorProfileResourceType":
      return {
        type: "Author Profile",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "CategoryResourceType":
      return {
        type: "Category",
        editURL: `${communityApiOrigin}/wp-admin/term.php?taxonomy=category&tag_ID=${node.wpId}`,
      };
    case "VideoGameResourceType":
      return {
        type: "Game",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "QuickPostResourceType":
      return {
        type: "QuickPost",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "VenueResourceType":
      return {
        type: "Venue",
        editURL: `${primaryCommunityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "MapResourceType":
      return {
        type: "Map",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "NeighborhoodResourceType":
      return {
        type: "Neighborhood",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "DiningReportResourceType":
      return {
        type: "Dining Report",
        editURL: `${communityApiOrigin}/wp-admin/post.php?post=${node.wpId}&action=edit`,
      };
    case "%other":
      return null;
  }
}

/**
 * Admin adds the Admin Menu and Shortcuts depending
 * on what type we are looking at (Entry, EntryGroup, etc...)
 */
const AdminContainer: FunctionComponent<Props> = ({ resolvedResource }) => {
  const {
    communityConfig: {
      communityApiOrigin,
      primaryCommunityApiOrigin: primaryCommunityApiOriginFromConfig,
    },
    appConfig: { deployEnv },
    featureConfig: { useQuickPosts },
  } = useConfigContext();

  const primaryCommunityApiOrigin = primaryCommunityApiOriginFromConfig;

  const node = useFragment(
    /**
     * @excludeCacheKey: nothing in this fragment should include cache keys as it exists
     * only for dev and editorial tooling purposes, inclusion of cache keys should be
     * dictated by the layout in which this fragment is used.
     */
    graphql`
      fragment AdminContainer_resolvedResource on ResolvedResourceInterface {
        __typename
        id
        wpId
        globalId

        ... on ContentResourceInterface {
          chorusId
          chorusUuid
          featuredImage {
            image @excludeCacheKey {
              originalUrl
              wpId
            }
          }

          ... on FeedContentResourceInterface {
            primaryCategory @excludeCacheKey {
              id
            }
          }
        }
        ... on NeighborhoodResourceType {
          id
          wpId
        }
      }
    `,
    resolvedResource || null,
  );

  const themeCtx = useTheme();

  const [visible, setVisibility] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [open, setOpen] = useState(false);
  const onOpen = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };
  const auth = useAuth();

  const editContext = determineEditContext({
    communityApiOrigin,
    primaryCommunityApiOrigin,
    node,
  });

  const resourceApiUrl = getResourceApiUrl({ node });

  useEffect(() => {
    let input = "";
    function onKeyUp(event: KeyboardEvent) {
      const key = event.key;

      if ((key && key.length !== 1) || event.target !== document.body) {
        return;
      }

      const phrase = !visible ? ADMIN_SECRET : ADMIN_DEV_PHRASE;

      if (visible && editContext && key === ADMIN_EDIT_SHORTCUT) {
        window.open(editContext.editURL, "_blank");
      }

      if (visible && devMode) {
        // Nothing to do.
        return;
      }

      input += key;

      // check if the input matches so far, or if the secret is being restarted
      if (input !== phrase.slice(0, input.length)) {
        if (key === phrase[0]) {
          input = key;
        } else {
          input = "";
        }
      } else if (input.length === phrase.length) {
        input = ""; // Reset input.
        if (phrase === ADMIN_SECRET) {
          setVisibility(true);
          setCookie(ADMIN_GEAR_COOKIE, "visible", 365);
        } else {
          setDevMode(true);
          setCookie(ADMIN_GEAR_COOKIE, "dev", 365);
        }
      }
    }

    switch (getCookie(ADMIN_GEAR_COOKIE)) {
      case "dev":
        setVisibility(true);
        setDevMode(true);
        break;
      case "visible":
        setVisibility(true);
        break;
    }

    document.body.addEventListener("keyup", onKeyUp);

    return () => {
      document.body.removeEventListener("keyup", onKeyUp);
    };
  }, [devMode, editContext, visible]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={styles.container}
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      onMouseOver={onOpen}
      onMouseLeave={onClose}
    >
      {
        <CogIcon
          className={clsx(
            open && styles.cogHidden,
            devMode && styles.cogDevMode,
          )}
        />
      }
      {open && (
        <>
          <h2 className={styles.header}>Quick Admin</h2>
          <ul>
            {useQuickPosts && (
              <AdminLinkItem
                href={`${communityApiOrigin}/wp-admin/post-new.php?post_type=vm_quickpost`}
              >
                New QuickPost
              </AdminLinkItem>
            )}
            {
              <AdminLinkItem
                href={`${communityApiOrigin}/wp-admin/post-new.php`}
              >
                New Post
              </AdminLinkItem>
            }
            {editContext && (
              <AdminLinkItem href={editContext.editURL}>
                Edit {editContext.type}
              </AdminLinkItem>
            )}
            {resourceApiUrl && (
              <AdminLinkItem href={resourceApiUrl}>
                View Resource API
              </AdminLinkItem>
            )}
            <AdminLinkItem href={`${communityApiOrigin}/wp-admin/edit.php`}>
              Dashboard
            </AdminLinkItem>
            <AdminLinkItem
              href={`${communityApiOrigin}/wp-admin/edit-tags.php?taxonomy=category`}
            >
              Categories
            </AdminLinkItem>
            {node && "featuredImage" in node && node.featuredImage && (
              <AdminLinkItem href={node.featuredImage.image.originalUrl}>
                View Original Image
              </AdminLinkItem>
            )}
            {node && "featuredImage" in node && node.featuredImage && (
              <AdminLinkItem
                href={`${communityApiOrigin}/wp-admin/upload.php?item=${node.featuredImage.image.wpId}`}
              >
                Edit Image
              </AdminLinkItem>
            )}
            {CORAL_ORIGIN && auth.viewer?.coral && (
              <>
                <AdminLinkItem
                  href={`${CORAL_ORIGIN}/admin/moderate#accessToken=${encodeURIComponent(
                    auth.viewer.coral,
                  )}`}
                >
                  Comment Moderation
                </AdminLinkItem>
              </>
            )}
          </ul>
          {node && devMode && (
            <section className={styles.devModeSection}>
              <h2 className={styles.header}>Dev</h2>
              <ul>
                {"wpId" in node && (
                  <AdminDevItem name="Wp Id" value={node.wpId} />
                )}
                {"id" in node && (
                  <AdminDevItem name="Node Id" value={node.id} base64 />
                )}
                {"globalId" in node && (
                  <AdminDevItem name="Global Id" value={node.globalId} />
                )}
                {"primaryCategory" in node && node.primaryCategory && (
                  <AdminDevItem
                    name="Primary Catgory Id"
                    value={node.primaryCategory.id}
                    base64
                  />
                )}
                {"chorusId" in node && node.chorusId && (
                  <AdminDevItem name="Legacy Id" value={node.chorusId} />
                )}
                {"chorusUuid" in node && node.chorusUuid && (
                  <AdminDevItem name="Legacy UUID" value={node.chorusUuid} />
                )}
                <AdminDevItem name="Deploy Env" value={deployEnv} />
              </ul>
            </section>
          )}
          {themeCtx?.themeSwitcherComponent}
        </>
      )}
    </div>
  );
};

export default withGracefulErrorBoundary(AdminContainer, {
  context: "AdminContainerErrorBoundary",
});
